import {
	type CollisionDetection,
	type DragCancelEvent,
	type DragEndEvent,
	type DragStartEvent,
	MouseSensor,
	pointerWithin,
	TouchSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import { useState } from 'react'
import { useFormBuilderStore } from '../store'
import type { ComponentDefinition } from '../store/componentRegistry'
import type { FormComponentName } from '../types'
export interface DragData {
	id?: string
	isNew?: boolean
	type?: string
	component?: ComponentDefinition
	containerId?: string | null
	sourceContainerId?: string | null
	sourceIndex?: number
	componentType?: FormComponentName | string
	index?: number
	column?: number
}

export function useFormBuilderDrag() {
	const [activeId, setActiveId] = useState<string | null>(null)
	const [activeDragData, setActiveDragData] = useState<DragData | null>(null)
	const setIsDragging = useFormBuilderStore((state) => state.setIsDragging)
	const addComponent = useFormBuilderStore((state) => state.addComponent)
	const moveComponent = useFormBuilderStore((state) => state.moveComponent)
	const componentRegistry = useFormBuilderStore((state) => state.componentRegistry)

	const mouseSensor = useSensor(MouseSensor, {
		activationConstraint: {
			distance: 10,
		},
	})

	const touchSensor = useSensor(TouchSensor, {
		activationConstraint: {
			delay: 150,
			tolerance: 8,
		},
	})

	const sensors = useSensors(mouseSensor, touchSensor)

	const handleDragStart = (event: DragStartEvent) => {
		const { active } = event
		const activeId = active.id.toString()
		setActiveId(activeId)
		const data = active.data.current as DragData
		const isComponentDrag = activeId.startsWith('component-')
		if (isComponentDrag) {
			const componentId = activeId.replace('component-', '')
			useFormBuilderStore.getState().selectComponent(componentId)
		}

		setActiveDragData(data)

		if (data?.isNew && data.type) {
			const componentDef = componentRegistry.find(
				(c) => c.name === data.type && c.defaultProps.isSection === data.component?.defaultProps.isSection,
			)
			if (componentDef) {
				setActiveDragData({
					...data,
					component: componentDef,
				})
			}
		}

		setIsDragging(true)
	}

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event

		if (over) {
			const activeData = active.data.current as DragData
			const overData = over.data.current as DragData

			const containerId = overData.containerId as string | undefined
			if (containerId) {
				overData.containerId = containerId
			}

			const isContainerDropzone = over.id.toString().startsWith('cdz-')
			if (isContainerDropzone) {
				const dropzoneId = over.id.toString()
				// Container IDs are UUIDs which won't contain "-col" followed by digits,
				// so the lazy .+? safely captures the full container ID.
				const colMatch = dropzoneId.match(/^cdz-(.+?)-col(\d+)-(\d+)$/)

				if (colMatch) {
					// Column-aware drop zone: cdz-{containerId}-col{colIdx}-{childIdx}
					overData.containerId = colMatch[1]
					const parsedCol = Number.parseInt(colMatch[2], 10)
					const parsedIdx = Number.parseInt(colMatch[3], 10)
					if (!Number.isNaN(parsedCol)) overData.column = parsedCol
					if (!Number.isNaN(parsedIdx)) overData.index = parsedIdx
				} else if (!containerId) {
					// Legacy drop zone: cdz-{containerId}-{index}
					const parts = dropzoneId.split('-')
					if (parts.length >= 3) {
						const extractedId = parts.slice(1, parts.length - 1).join('-')
						const index = Number.parseInt(parts[parts.length - 1], 10)

						overData.containerId = extractedId
						if (!Number.isNaN(index)) {
							overData.index = index
						}
					}
				}
			}
			if (activeData && overData && activeData.isNew && activeData.type) {
				addComponent(
					activeData.type as FormComponentName,
					overData.containerId as string | undefined,
					overData.index as number | undefined,
					activeData.component?.defaultProps.isSection as boolean | undefined,
					overData.column as number | undefined,
				)
			} else if (activeData && overData && active.id.toString().startsWith('component-')) {
				const componentId = active.id.toString().replace('component-', '')

				const isChangingParent = activeData.sourceContainerId !== overData.containerId

				const isChangingColumn = activeData.column !== overData.column

				const isSameParentReordering =
					activeData.sourceContainerId === overData.containerId && activeData.sourceIndex !== overData.index

				if (isChangingParent || isSameParentReordering || isChangingColumn) {
					moveComponent(
						componentId,
						overData.containerId as string,
						overData.index ?? 0,
						overData.column as number | undefined,
					)
				}
			}
		}

		setActiveId(null)
		setActiveDragData(null)
		setIsDragging(false)
	}

	const handleDragCancel = (_event: DragCancelEvent) => {
		setActiveId(null)
		setActiveDragData(null)
		setIsDragging(false)
	}

	const customCollisionDetection: CollisionDetection = (args) => {
		const intersections = pointerWithin(args)

		if (intersections.length === 0) return []

		const containerDropzones = intersections.filter((intersection) => intersection.id.toString().startsWith('cdz-'))

		if (containerDropzones.length > 0) {
			return [containerDropzones[0]]
		}

		return intersections
	}

	return {
		activeId,
		activeDragData,
		sensors,
		customCollisionDetection,
		handleDragStart,
		handleDragEnd,
		handleDragCancel,
	}
}
